import { MAX_CACHE_TIME, StorageOptionsStatus } from '../../Constant'
import { executeWithRetry, withTimeout } from '../networking/networkHelpers'
import type { IndexedDBAPI } from './indexedDB'

export type CacheEnvelope<T = unknown> = {
  data: T
  lastUpdated: string
  ttlMs: number
}

/**
 * Check if the cache has expired
 * @param lastUpdated - The last updated date of the cache
 * @param ttlMs - The time to live in milliseconds
 * @returns True if the cache has expired, false otherwise
 */
export const checkExpiredCache = (lastUpdated: string, ttlMs?: number) => {
  try {
    const lastUpdatedDate = new Date(lastUpdated).getTime()
    if (isNaN(lastUpdatedDate)) {
      // Invalid date, expired - trigger refresh
      return true
    }
    const now = new Date().getTime()
    const timeSinceUpdate = now - lastUpdatedDate
    return timeSinceUpdate > (ttlMs ?? MAX_CACHE_TIME)
  } catch (error) {
    console.error('Error checking cache expiration:', error)
    return true
  }
}

/**
 * Check if the browser supports the storage.estimate API
 */
const supportsEstimate =
  typeof navigator !== 'undefined' && !!navigator.storage?.estimate

/**
 * Check if the browser has enough space to store the data
 * @param bytesNeeded - The number of bytes needed to store the data
 * @returns True if the browser has enough space, false otherwise
 */
export const ensureSpace = async (bytesNeeded: number): Promise<boolean> => {
  if (!supportsEstimate) return false
  try {
    const est = await navigator.storage.estimate()
    if (
      typeof est.quota === 'number' &&
      typeof est.usage === 'number' &&
      est.quota - est.usage < bytesNeeded
    ) {
      console.warn('Insufficient client storage')
      return false
    }
    return true
  } catch (err) {
    console.warn('Storage estimate failed; proceeding without space check', err)
    return false
  }
}

/**
 * Conservative size estimation for storage actions
 * @param input - The data to estimate the size of
 * @returns The estimated size in bytes
 */
export const estimatedSizeBytes = (input: unknown): number => {
  try {
    const json = JSON.stringify(input)
    const utf8 = new TextEncoder().encode(json)
    return utf8.byteLength + 128
  } catch {
    return 1024
  }
}

/**
 * Functions for storing data in caches
 * @param api - The IndexedDB API to use
 * @returns An object with functions for storing and retrieving data from caches
 */
export function makeStorageHelpers(api: IndexedDBAPI<CacheEnvelope<unknown>>) {
  const deleteFromCache = async (key: string, storageStatuses: StorageOptionsStatus) => {
    const { indexedDB, local, session } = storageStatuses
    const deletions: Promise<void>[] = []

    if (indexedDB) {
      deletions.push(
        api.del(key).catch((err) => {
          console.error(`IndexedDB deletion failed for "${key}":`, err)
        })
      )
    }
    if (local) {
      deletions.push(
        Promise.resolve()
          .then(() => {
            localStorage.removeItem(key)
          })
          .catch((error) => {
            console.error(`LocalStorage deletion failed for key "${key}":`, error)
          })
      )
    }
    if (session) {
      deletions.push(
        Promise.resolve()
          .then(() => {
            sessionStorage.removeItem(key)
          })
          .catch((error) => {
            console.error(`SessionStorage deletion failed for key "${key}":`, error)
          })
      )
    }

    await Promise.allSettled(deletions)
  }

  const storeInCache = async (
    key: string,
    item: unknown,
    storageStatuses: StorageOptionsStatus,
    ttlMs: number = MAX_CACHE_TIME
  ): Promise<void> => {
    const { indexedDB, local, session } = storageStatuses
    if (!indexedDB && !local && !session) return

    // Validate and sanitize
    if (item === undefined || item === null) {
      console.warn(`Attempting to store null/undefined value for key: ${key}`)
      return
    }
    const itemStr = JSON.stringify(item)
    if (
      itemStr.includes('password') ||
      itemStr.includes('token') ||
      itemStr.includes('secret')
    ) {
      console.error('Attempted to store potentially sensitive data')
      return
    }

    const wrapped: CacheEnvelope<unknown> = {
      data: item,
      lastUpdated: new Date().toISOString(),
      ttlMs,
    }

    if (indexedDB) {
      try {
        const res = await executeWithRetry(
          async () =>
            await withTimeout(
              `store in indexedDB: ${key}`,
              api.set(key, wrapped as CacheEnvelope<unknown>),
              10000
            ),
          `store in indexedDB: ${key}`
        )
        if (res !== null) return
      } catch (err) {
        console.error(`IndexedDB storage failed for "${key}":`, err)
      }
    }
    if (local) {
      try {
        const res = await executeWithRetry(
          async () =>
            await Promise.resolve(localStorage.setItem(key, JSON.stringify(wrapped))),
          `store in local storage: ${key}`
        )
        if (res !== null) return
      } catch (e: unknown) {
        const error =
          e instanceof Error && e.name === 'QuotaExceededError'
            ? `localStorage quota exceeded for key: ${key}`
            : `localStorage error for key: ${key}`
        console.error(`${error}:`, e)
      }
    }
    if (session) {
      try {
        const res = await executeWithRetry(
          async () =>
            await Promise.resolve(sessionStorage.setItem(key, JSON.stringify(wrapped))),
          `store in session storage: ${key}`
        )
        if (res !== null) return
      } catch (e: unknown) {
        const error =
          e instanceof Error && e.name === 'QuotaExceededError'
            ? `sessionStorage quota exceeded for key: ${key}`
            : `sessionStorage error for key: ${key}`
        console.error(`${error}:`, e)
      }
    }
  }

  const retrieveFromCache = async (
    key: string,
    storageStatuses: StorageOptionsStatus,
    customTTL?: number
  ): Promise<{ data: unknown | null }> => {
    const { indexedDB, local, session } = storageStatuses
    if (!indexedDB && !local && !session) return { data: null }

    const validate = (data: unknown): { data: unknown | null } => {
      const e = data as Partial<CacheEnvelope<unknown>> | null
      if (!e || typeof e !== 'object' || e.data === undefined || !e.lastUpdated)
        return { data: null }
      const ttlToUse = customTTL ?? e.ttlMs ?? MAX_CACHE_TIME
      if (checkExpiredCache(e.lastUpdated, ttlToUse)) {
        void deleteFromCache(key, storageStatuses)
        return { data: null }
      }
      return { data: e.data }
    }

    if (indexedDB) {
      try {
        const value = await executeWithRetry(
          () => withTimeout(`retrieve from indexedDB: ${key}`, api.get(key), 10000),
          `retrieve from indexedDB: ${key}`
        )
        const validatedData = validate(value)
        if (validatedData?.data !== null) return validatedData
      } catch (error) {
        console.error(`DB retrieval failed for key "${key}":`, error)
      }
    }
    if (local) {
      try {
        const value = await executeWithRetry(async () => {
          const raw = localStorage.getItem(key)
          if (!raw) return null
          try {
            return JSON.parse(raw)
          } catch {
            localStorage.removeItem(key)
            return null
          }
        }, `retrieve from local storage: ${key}`)
        const validatedData = validate(value)
        if (validatedData?.data !== null) return validatedData
      } catch (err) {
        console.error(`LocalStorage retrieval failed for "${key}":`, err)
      }
    }

    if (session) {
      try {
        const value = await executeWithRetry(async () => {
          const raw = sessionStorage.getItem(key)
          if (!raw) return null
          try {
            return JSON.parse(raw)
          } catch {
            sessionStorage.removeItem(key)
            return null
          }
        }, `retrieve from session storage: ${key}`)
        const validatedData = validate(value)
        if (validatedData?.data !== null) return validatedData
      } catch (err) {
        console.error(`SessionStorage retrieval failed for "${key}":`, err)
      }
    }

    return { data: null }
  }

  return { storeInCache, retrieveFromCache, deleteFromCache }
}
